import logoDark from "assets/images/logo-dark.png";
import logoLight from "assets/images/logo-light.png";
import withRouter from "Common/withRouter";
import { useFormik as useFormic } from "formik";
import { AppDispatch } from "store";
import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { createSelector } from "reselect";
import { loginAndFetchData } from "slices/thunk";
import * as Yup from "yup";

interface UserData {
  ownerDocument?: string;
  username?: string;
  password?: string;
}

interface LoginState {
  success?: boolean;
  error?: string;
}

interface RegisterState {
  user?: UserData;
}

interface RootState {
  Register?: RegisterState;
  Login?: LoginState;
}

interface LoginFormValues {
  proprietario: string;
  cpfCnpj: string;
  password: string;
}

interface LoginProps {
  router: {
    navigate: (path: string) => void;
  };
}

const Login = (props: LoginProps) => {
  document.title = "Login | DAV - online CoffCode";

  const dispatch = useDispatch<AppDispatch>();

  const selectLogin = createSelector(
    (state: RootState) => state.Login,
    (login) => ({
      success: login?.success || false,
      error: login?.error || "",
    })
  );

  const { success, error } = useSelector(selectLogin);
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const formatCpfCnpj = (value: string): string => {
    const digits = value.replace(/\D/g, "");
    if (digits.length <= 11) {
      return digits
        .replace(/(\d{3})(\d)/, "$1.$2")
        .replace(/(\d{3})(\d)/, "$1.$2")
        .replace(/(\d{3})(\d{1,2})$/, "$1-$2");
    }
    return digits
      .replace(/(\d{2})(\d)/, "$1.$2")
      .replace(/(\d{3})(\d)/, "$1.$2")
      .replace(/(\d{3})(\d)/, "$1/$2")
      .replace(/(\d{4})(\d{1,2})$/, "$1-$2");
  };

  const validation = useFormic<LoginFormValues>({
    enableReinitialize: true,

    initialValues: {
      proprietario: "",
      cpfCnpj: "",
      password: "",
    },
    validationSchema: Yup.object({
      proprietario: Yup.string().required("Por favor digite o CPF/CNPJ do proprietário"),
      cpfCnpj: Yup.string().required("Por favor digite o usuário"),
      password: Yup.string().required("Por favor digite sua senha"),
    }),
    onSubmit: async (values: LoginFormValues) => {
      setIsSubmitting(true);
      try {
        const sanitized = {
          ...values,
          proprietario: values.proprietario.replace(/\D/g, ""),
        };
        await dispatch(loginAndFetchData(sanitized)).unwrap();
        props.router.navigate("/dashboard");
      } catch (error) {
        console.error("Erro no login:", error);
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  React.useEffect(() => {
    const bodyElement = document.body;

    bodyElement.classList.add(
      "flex",
      "items-center",
      "justify-center",
      "min-h-screen",
      "py-16",
      "lg:py-10",
      "bg-slate-50",
      "dark:bg-zink-800",
      "dark:text-zink-100",
      "font-public"
    );

    return () => {
      bodyElement.classList.remove(
        "flex",
        "items-center",
        "justify-center",
        "min-h-screen",
        "py-16",
        "lg:py-10",
        "bg-slate-50",
        "dark:bg-zink-800",
        "dark:text-zink-100",
        "font-public"
      );
    };
  }, []);

  return (
    <div className="relative">
      <div className="mb-0 w-screen lg:mx-auto lg:w-[500px] card shadow-lg border-none shadow-slate-100 relative">
        <div className="!px-10 !py-12 card-body">
          <Link to="/">
            <img src={logoLight} alt="" className="hidden mx-auto h-80 dark:block" />
            <img src={logoDark} alt="" className="block mx-auto h-80 dark:hidden" />
          </Link>

          <div className="mt-8 text-center">
            <h4 className="mb-1 text-custom-500 dark:text-custom-500">Bem vindo !</h4>
            <p className="text-slate-500 dark:text-zink-200">
              Faça seu login e siga pra o DAV - online.
            </p>
          </div>

          <form
            className="mt-10"
            id="signInForm"
            onSubmit={(event: React.FormEvent<HTMLFormElement>) => {
              event.preventDefault();
              validation.handleSubmit();
              return false;
            }}
          >
            {success && (
              <div
                className="px-4 py-3 mb-3 text-sm text-green-500 border border-green-200 rounded-md bg-green-50 dark:bg-green-400/20 dark:border-green-500/50"
                id="loginSuccessAlert"
              >
                Login efetuado com sucesso.
              </div>
            )}
            {error && (
              <div
                className="px-4 py-3 mb-3 text-sm text-red-500 border border-red-200 rounded-md bg-red-50 dark:bg-red-400/20 dark:border-red-500/50"
                id="loginErrorAlert"
              >
                Falha ao efetuar login.
              </div>
            )}
            <div className="mb-3">
              <label htmlFor="proprietario" className="inline-block mb-2 text-base font-medium">
                CPF/CNPJ *
              </label>
              <input
                type="text"
                id="proprietario"
                name="proprietario"
                className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                placeholder="000.000.000-00 ou 00.000.000/0000-00"
                maxLength={18}
                onChange={(e) => {
                  const formatted = formatCpfCnpj(e.target.value);
                  validation.setFieldValue("proprietario", formatted);
                }}
                onBlur={validation.handleBlur}
                value={validation.values.proprietario || ""}
              />
              {validation.touched.proprietario && validation.errors.proprietario ? (
                <div id="proprietario-error" className="mt-1 text-sm text-red-500">
                  {validation.errors.proprietario}
                </div>
              ) : null}
            </div>
            <div className="mb-3">
              <label htmlFor="cpfCnpj" className="inline-block mb-2 text-base font-medium">
                Usuário
              </label>
              <input
                type="text"
                id="cpfCnpj"
                name="cpfCnpj"
                className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                placeholder="Digite o seu usuário"
                onChange={validation.handleChange}
                onBlur={validation.handleBlur}
                value={validation.values.cpfCnpj || ""}
              />
              {validation.touched.cpfCnpj && validation.errors.cpfCnpj ? (
                <div id="cpfCnpj-error" className="mt-1 text-sm text-red-500">
                  {validation.errors.cpfCnpj}
                </div>
              ) : null}
            </div>
            <div className="mb-3">
              <label htmlFor="password" className="inline-block mb-2 text-base font-medium">
                senha
              </label>
              <input
                type="password"
                id="password"
                name="password"
                className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                placeholder="Digite sua senha"
                onChange={validation.handleChange}
                onBlur={validation.handleBlur}
                value={validation.values.password || ""}
              />
              {validation.touched.password && validation.errors.password ? (
                <div id="password-error" className="mt-1 text-sm text-red-500">
                  {validation.errors.password}
                </div>
              ) : null}
            </div>
            <div>
              <div className="flex items-center gap-2">
                <input
                  id="checkboxDefault1"
                  className="border rounded-sm appearance-none size-4 bg-slate-100 border-slate-200 dark:bg-zink-600 dark:border-zink-500 checked:bg-custom-500 checked:border-custom-500 dark:checked:bg-custom-500 dark:checked:border-custom-500 checked:disabled:bg-custom-400 checked:disabled:border-custom-400"
                  type="checkbox"
                  value=""
                />
                <label
                  htmlFor="checkboxDefault1"
                  className="inline-block text-base font-medium align-middle cursor-pointer"
                >
                  Manter logado
                </label>
              </div>
            </div>
            <div className="mt-10">
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full text-white btn bg-custom-500 border-custom-500 hover:text-white hover:bg-custom-600 hover:border-custom-600 focus:text-white focus:bg-custom-600 focus:border-custom-600 focus:ring focus:ring-custom-100 active:text-white active:bg-custom-600 active:border-custom-600 active:ring active:ring-custom-100 dark:ring-custom-400/20 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Entrando...
                  </span>
                ) : (
                  "Entrar"
                )}
              </button>
            </div>

            <div className="mt-10 text-center">
              <p className="mb-0 text-slate-500 dark:text-zink-200">
                Esqueceu sua senha?{" "}
                <Link
                  to="/recover-password"
                  className="font-semibold underline transition-all duration-150 ease-linear text-slate-500 dark:text-zink-200 hover:text-custom-500 dark:hover:text-custom-500"
                >
                  {" "}
                  Clique aqui
                </Link>{" "}
              </p>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default withRouter(Login);
